import type * as React from "react";
import { type FC, type ReactNode, useCallback, useEffect, useRef } from "react";
import { styled } from "~/common/mui";

interface Props {
  excludeLastCol?: boolean;
  disabled?: boolean;
  children?: ReactNode;
}

export const TableHoverHighlight: FC<Props> = ({ children, disabled, excludeLastCol }) => {
  const rowHoverRef = useRef<HTMLDivElement>(null);
  const colHoverRef = useRef<HTMLDivElement>(null);

  const showHighlight = useCallback((e: React.MouseEvent<HTMLElement>) => {
    // TS not inferring properly
    const target = e.target as HTMLElement;
    const tableElement = target.closest(".scha__highlight-hover-table td") as HTMLTableCellElement | null;
    const scrollContainer = tableElement?.closest("table")?.parentElement;

    if (
      !tableElement ||
      (excludeLastCol && tableElement.parentElement?.lastElementChild === tableElement) ||
      !scrollContainer
    ) {
      return;
    }

    const scrollLeft = scrollContainer.scrollLeft;
    const left = tableElement.offsetLeft;
    const offsetParent = tableElement.offsetParent as HTMLElement;
    const top = tableElement.offsetTop;
    const cellHeight = tableElement.offsetHeight;
    const cellWidth = tableElement.offsetWidth;

    if (rowHoverRef.current) {
      const style = rowHoverRef.current.style;
      style.visibility = "visible";
      style.height = cellHeight + "px";
      if (tableElement.offsetParent?.tagName === "TABLE") {
        style.top = `${top + offsetParent.offsetTop}px`;
      } else {
        style.top = `${top}px`;
      }
    }
    if (colHoverRef.current) {
      const headerHeight = tableElement.closest("table")?.querySelector("th")?.offsetHeight ?? 1;
      const style = colHoverRef.current.style;
      style.visibility = "visible";
      style.width = cellWidth + "px";
      style.bottom = "0px";
      style.top = `${headerHeight - 1}px`; // so the border doesn't look thicker
      style.left = left - scrollLeft + "px";
    }
  }, []);

  const hideHighlight = useCallback(() => {
    if (rowHoverRef.current) {
      rowHoverRef.current.style.visibility = "hidden";
    }
    if (colHoverRef.current) {
      colHoverRef.current.style.visibility = "hidden";
    }
  }, []);

  useEffect(() => {
    if (disabled) {
      hideHighlight();
    }
  }, [disabled]);

  return (
    <HighlightTableRoot className={"scha__highlight-hover-table"}>
      <div onMouseOver={!disabled ? showHighlight : undefined} onMouseOut={hideHighlight}>
        {children}
      </div>
      <HighlightOverlay className={"scha__highlight-hover-table__col-hover"} ref={colHoverRef} />
      <HighlightOverlay className={"scha__highlight-hover-table__row-hover"} ref={rowHoverRef} />
    </HighlightTableRoot>
  );
};

const HighlightTableRoot = styled("div")({
  position: "relative",
});

const HighlightOverlay = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  width: "100%",
  border: `1px solid ${theme.palette.divider}`,
  visibility: "hidden",
  pointerEvents: "none",
  zIndex: 100,
}));
